import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AUTH_ROLES } from '../types/auth.type';
import { getSyncPreference, toggleSyncPreference, checkAutoSync } from '../services/layout.service';

/**
 * Custom hook to manage sync preference for layout customization
 * @param {string} pageType - The current page type (hospital, admission, etc.)
 * @returns {Object} - Object containing sync preference state and handlers
 */
const useSyncPreference = (pageType) => {
  const [syncPreference, setSyncPreference] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wasAutoSynced, setWasAutoSynced] = useState(false);
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);
  
  const authUser = useSelector((state) => state.auth);
  const isSuperUser = authUser?.role?.slug === AUTH_ROLES.SUPER;

  // Fetch sync preference from database
  const fetchSyncPreference = useCallback(async () => {
    if (!isSuperUser || !pageType) {
      return;
    }

    if (!isMountedRef.current) return;
    setLoading(true);
    setError(null);
    
    try {
      const response = await getSyncPreference(pageType);
      if (!isMountedRef.current) return; // Check again after async operation
      
      const newSyncPreference = response.syncWithDefault;
      setSyncPreference(newSyncPreference);
    } catch (error) {
      if (!isMountedRef.current) return; // Check again after async operation
      console.error('Error fetching sync preference:', error);
      setError(error.message || 'Failed to fetch sync preference');
      setSyncPreference(false);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [isSuperUser, pageType]);

  // Toggle sync preference
  const toggleSyncPreferenceHandler = useCallback(async (newValue) => {
    if (!isSuperUser || !pageType) {
      console.error('Cannot toggle sync preference: not super user or no pageType');
      return;
    }

    if (!isMountedRef.current) return;
    setLoading(true);
    setError(null);
    
    try {
      await toggleSyncPreference(pageType, newValue);
      if (!isMountedRef.current) return; // Check again after async operation
      setSyncPreference(newValue);
    } catch (error) {
      if (!isMountedRef.current) return; // Check again after async operation
      console.error('Error toggling sync preference:', error);
      setError(error.message || 'Failed to toggle sync preference');
      // Don't update local state if API call failed
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [isSuperUser, pageType]);

  // Fetch sync preference on mount and when pageType changes
  useEffect(() => {
    fetchSyncPreference();
  }, [fetchSyncPreference]);

  // Check for auto-sync status
  const checkAutoSyncStatus = useCallback(async () => {
    if (!isSuperUser || !pageType) {
      return;
    }

    if (!isMountedRef.current) return;

    try {
      const response = await checkAutoSync(pageType);
      if (!isMountedRef.current) return; // Check again after async operation
      
      // Add safety check for response structure
      if (response && response.data && typeof response.data.wasAutoSynced !== 'undefined') {
        setWasAutoSynced(response.data.wasAutoSynced);
      } else {
        console.warn('Auto-sync response missing expected data structure:', response);
        setWasAutoSynced(false); // Default to false if data is missing
      }
    } catch (error) {
      if (!isMountedRef.current) return; // Check again after async operation
      console.error('Error checking auto-sync status:', error);
      setWasAutoSynced(false); // Default to false on error
    }
  }, [isSuperUser, pageType]);

  // Check auto-sync status on mount
  useEffect(() => {
    checkAutoSyncStatus();
  }, [checkAutoSyncStatus]);

  // Cleanup effect to prevent state updates after unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    syncPreference,
    loading,
    error,
    isSuperUser,
    wasAutoSynced,
    toggleSyncPreference: toggleSyncPreferenceHandler,
    refetch: fetchSyncPreference,
    checkAutoSyncStatus
  };
};

export default useSyncPreference;
